'use client'

import { useState, useEffect, useRef } from 'react'
import { MessageBubble } from './MessageBubble'
import { TaskCard } from './TaskCard'
import { SubtaskTree } from './SubtaskTree'
import { useTaskStore } from '../../stores/taskStore'

// ============================================================================
// Types
// ============================================================================

interface Message {
  id: string
  type: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  taskId?: string
}

interface ChatWindowProps {
  className?: string
  onTaskCreated?: (taskId: string) => void
  autoCreateSubtasks?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function ChatWindow({
  className = '',
  onTaskCreated,
  autoCreateSubtasks = true
}: ChatWindowProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'system',
      content: '👋 Bem-vindo ao Meu Claude 24/7 UI! Descreva a task que deseja criar.',
      timestamp: new Date()
    }
  ])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const { createTask, getTasks } = useTaskStore()

  // Auto-scroll para última mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!input.trim()) return

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: input,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInput('')
    setIsLoading(true)

    try {
      // Criar task via API
      const response = await fetch('https://api.lldonha.com/api/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          titulo: input.slice(0, 100), // Primeiras 100 chars como título
          prompt: input,
          prioridade: 5,
          source: 'web',
          username: 'Lucas'
        })
      })

      const data = await response.json()

      if (data.success) {
        const taskId = data.task_id

        // Mensagem de sucesso
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: `✅ Task criada com sucesso!\n\nID: ${taskId.slice(0, 8)}...\n\n${autoCreateSubtasks ? '🤖 Criando subtasks automaticamente...' : '📋 Task aguardando processamento'}`,
          timestamp: new Date(),
          taskId
        }

        setMessages(prev => [...prev, assistantMessage])

        // Callback
        onTaskCreated?.(taskId)

        // Atualizar store
        await getTasks()

      } else {
        throw new Error('Falha ao criar task')
      }

    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 2).toString(),
        type: 'system',
        content: `❌ Erro ao criar task: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e as any)
    }
  }

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className={`flex flex-col h-full bg-gray-50 dark:bg-gray-900 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            💬 Chat Interface
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Crie e gerencie tasks via chat
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={autoCreateSubtasks}
            onChange={(e) => {
              // TODO: Implement auto-create subtasks toggle
            }}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-gray-700 dark:text-gray-300">
            Auto-subtasks
          </span>
        </label>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {messages.map((message) => (
          <div key={message.id}>
            <MessageBubble
              type={message.type}
              content={message.content}
              timestamp={message.timestamp}
            />

            {/* Mostrar TaskCard se mensagem tem taskId */}
            {message.taskId && (
              <div className="mt-3 ml-12">
                <TaskCard taskId={message.taskId} showSubtasks />
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex items-center gap-2 text-gray-500">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-sm">Processando...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="px-6 py-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <form onSubmit={handleSubmit} className="flex gap-3">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Descreva sua task aqui... (Enter para enviar, Shift+Enter para nova linha)"
            disabled={isLoading}
            className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 text-gray-900 dark:text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            rows={3}
          />

          <button
            type="submit"
            disabled={isLoading || !input.trim()}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {isLoading ? '...' : 'Enviar'}
          </button>
        </form>

        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          💡 Dica: Seja específico sobre o que deseja implementar para que o worker possa executar com precisão
        </p>
      </div>
    </div>
  )
}
